<!-- Рисует линейный график с легендой и tooltip для dashboard-метрик. -->
<template>
  <div class="line-chart" ref="wrap">
    <!-- Легенда с чекбоксами -->
    <div class="line-chart__legend">
      <BaseCheckbox
        v-for="s in normalizedSeries"
        :key="'cb-' + s.key"
        :model-value="!hiddenSeries[s.key]"
        @update:modelValue="(val) => setSeriesVisible(s.key, val)"
      >
        <span class="legend-item">
          <span
            class="legend-dot"
            :style="{ background: s.color }"
          ></span>
          {{ s.label }}
        </span>
      </BaseCheckbox>
    </div>

    <svg
      v-if="chartData"
      :viewBox="`0 0 ${chartData.W} ${chartData.H}`"
      class="line-chart__svg"
      @mousemove="onMouseMove"
      @mouseleave="tooltip.visible = false"
    >
      <defs>
        <linearGradient
          v-for="s in normalizedSeries"
          :key="'grad-' + s.key"
          :id="'grad-' + s.key + '_' + uid"
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop
            offset="0%"
            :stop-color="s.color"
            stop-opacity="0.18"
          />
          <stop
            offset="100%"
            :stop-color="s.color"
            stop-opacity="0"
          />
        </linearGradient>
      </defs>

      <!-- Сетка -->
      <line
        v-for="gl in chartData.gridLines"
        :key="'gl-' + gl.y"
        :x1="chartData.padL"
        :y1="gl.y"
        :x2="chartData.W - chartData.padR"
        :y2="gl.y"
        stroke="#e5e7eb"
        stroke-width="1"
      />
      <text
        v-for="gl in chartData.gridLines"
        :key="'glt-' + gl.y"
        :x="chartData.padL - 8"
        :y="gl.y + 4"
        text-anchor="end"
        class="line-chart__axis-label"
      >
        {{ formatAxisValue(gl.value) }}
      </text>

      <!-- Подписи X — дата и время в столбик -->
      <text
        v-for="pt in chartData.labelPoints"
        :key="'xl-' + pt.index"
        :x="pt.x"
        :y="chartData.H - chartData.padB + 14"
        text-anchor="middle"
        class="line-chart__axis-label"
      >
        <tspan :x="pt.x" dy="0">{{ pt.label.date }},</tspan>
        <tspan :x="pt.x" dy="12">{{ pt.label.time }}</tspan>
      </text>

      <!-- Заливки и линии -->
      <g
        v-for="s in normalizedSeries"
        v-show="!hiddenSeries[s.key]"
        :key="'series-' + s.key"
      >
        <path
          :d="chartData.areas[s.key]"
          :fill="`url(#grad-${s.key}_${uid})`"
        />
        <path
          :d="chartData.lines[s.key]"
          fill="none"
          :stroke="s.color"
          stroke-width="2"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
      </g>

      <!-- Точки — только ненулевые -->
      <g v-if="!hideDots">
        <g
          v-for="s in normalizedSeries"
          v-show="!hiddenSeries[s.key]"
          :key="'dots-' + s.key"
        >
          <circle
            v-for="pt in chartData.nonZeroPoints[s.key]"
            :key="'dot-' + pt.index"
            :cx="pt.x"
            :cy="pt.y"
            r="4"
            :fill="s.color"
            stroke="#fff"
            stroke-width="1.5"
          />
        </g>
      </g>

      <!-- Hit-зоны -->
      <rect
        v-for="(pt, i) in chartData.points"
        :key="'hit-' + i"
        :x="pt.x - chartData.hitW / 2"
        :y="chartData.padT"
        :width="chartData.hitW"
        :height="chartData.H - chartData.padT - chartData.padB"
        fill="transparent"
      />

      <!-- Вертикальная линия тултипа -->
      <line
        v-if="tooltip.visible"
        :x1="tooltip.svgX"
        :y1="chartData.padT"
        :x2="tooltip.svgX"
        :y2="chartData.H - chartData.padB"
        stroke="#d1d5db"
        stroke-width="1"
        stroke-dasharray="4 3"
      />
    </svg>

    <!-- Пустое состояние когда всё скрыто -->
    <div v-else-if="allHidden" class="line-chart__empty">
      Все серии скрыты — выберите хотя бы одну
    </div>

    <transition name="fade-slide">
      <div
        v-if="tooltip.visible"
        class="line-chart__tooltip"
        :style="{
          left: tooltip.left + 'px',
          top: tooltip.top + 'px',
        }"
      >
        <span class="line-chart__tooltip-period">
          {{ tooltip.label }}
        </span>
        <template v-for="s in normalizedSeries">
          <span
            v-if="
              !hiddenSeries[s.key] &&
              (tooltip.values[s.key] || 0) !== 0
            "
            :key="'tt-' + s.key"
            class="line-chart__tooltip-row"
          >
            <span
              class="line-chart__tooltip-dot"
              :style="{ background: s.color }"
            ></span>
            {{ s.label }}:
            {{ formatTooltipValue(tooltip.values[s.key]) }}
          </span>
        </template>
        <span
          v-if="tooltip.isEmpty"
          class="line-chart__tooltip-empty"
        >
          Нет данных
        </span>
      </div>
    </transition>
  </div>
</template>

<script>
const PAD = { T: 16, R: 16, B: 44, L: 60 };
const GRID_COUNT = 4;

let uidCounter = 0;

export default {
  name: "LineChart",

  props: {
    series: {
      type: Array,
      required: true,
    },
    points: {
      type: Array,
      required: true,
    },
    height: {
      type: Number,
      default: 240,
    },
    formatValue: {
      type: Function,
      default: null,
    },
    hideDots: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      uid: ++uidCounter,
      containerWidth: 600,
      hiddenSeries: {},
      tooltip: {
        visible: false,
        svgX: 0,
        left: 0,
        top: 0,
        label: "",
        values: {},
        isEmpty: false,
      },
    };
  },

  computed: {
    normalizedSeries() {
      return this.series.map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color || "#4c6ef5",
      }));
    },

    allHidden() {
      return this.normalizedSeries.every(
        (s) => this.hiddenSeries[s.key]
      );
    },

    chartData() {
      if (!this.points || this.points.length < 2 || this.allHidden)
        return null;

      const W = Math.max(this.containerWidth, 200);
      const H = this.height;
      const { T, R, B, L } = PAD;
      const innerW = W - L - R;
      const innerH = H - T - B;
      const n = this.points.length;

      const visibleSeries = this.normalizedSeries.filter(
        (s) => !this.hiddenSeries[s.key]
      );

      const allValues = this.points.flatMap((p) =>
        visibleSeries.map((s) => p.values[s.key] ?? 0)
      );
      const minVal = Math.min(
        0,
        ...(allValues.length ? allValues : [0])
      );
      const maxVal = Math.max(
        ...(allValues.length ? allValues : [0]),
        1
      );
      const range = maxVal - minVal || 1;

      const toY = (v) => T + innerH - ((v - minVal) / range) * innerH;
      const toX = (i) =>
        L + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
      const hitW = Math.max(innerW / n, 1);

      const points = this.points.map((p, i) => {
        const label = {
          date: p.label?.date || "",
          time: p.label?.time || "",
        };
        const values = p.values || {};

        return {
          index: i,
          x: toX(i),
          label, // { date, time }
          ys: Object.fromEntries(
            this.normalizedSeries.map((s) => [
              s.key,
              toY(values[s.key] ?? 0),
            ])
          ),
          values,
          hasData: visibleSeries.some(
            (s) => (values[s.key] ?? 0) !== 0
          ),
        };
      });

      const nonZeroPoints = points.filter((p) => p.hasData);

      // Динамический gap на основе длины самой широкой строки подписи (дата или время)
      const AVG_CHAR_WIDTH = (7 / this.containerWidth) * W;
      const maxLabelLen = nonZeroPoints.reduce((max, pt) => {
        const dateLength = pt.label?.date?.length || 0;
        const timeLength = pt.label?.time?.length || 0;

        return Math.max(max, dateLength, timeLength);
      }, 0);
      const MIN_LABEL_GAP =
        maxLabelLen * AVG_CHAR_WIDTH + (8 / this.containerWidth) * W;

      // Жадный алгоритм - добавляем подпись только если не накладывается
      const labelPoints = [];
      let lastLabelX = -Infinity;
      nonZeroPoints.forEach((pt) => {
        if (pt.x - lastLabelX >= MIN_LABEL_GAP) {
          labelPoints.push(pt);
          lastLabelX = pt.x;
        }
      });

      // Кружки-точки - только ненулевые, отдельно по каждой серии
      const nonZeroPointsBySeries = {};
      this.normalizedSeries.forEach((s) => {
        nonZeroPointsBySeries[s.key] = points
          .filter((p) => (p.values[s.key] ?? 0) !== 0)
          .map((p) => ({ index: p.index, x: p.x, y: p.ys[s.key] }));
      });

      const lines = {};
      const areas = {};
      const baseY = toY(0);

      this.normalizedSeries.forEach((s) => {
        const d = points
          .map(
            (p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.ys[s.key]}`
          )
          .join(" ");
        lines[s.key] = d;
        areas[s.key] = `${d} L ${points[n - 1].x} ${baseY} L ${
          points[0].x
        } ${baseY} Z`;
      });

      const step = range / GRID_COUNT;
      const gridLines = Array.from(
        { length: GRID_COUNT + 1 },
        (_, i) => ({
          y: toY(minVal + step * i),
          value: minVal + step * i,
        })
      );

      return {
        W,
        H,
        padT: T,
        padR: R,
        padB: B,
        padL: L,
        points,
        labelPoints,
        nonZeroPoints: nonZeroPointsBySeries,
        lines,
        areas,
        gridLines,
        hitW,
      };
    },
  },

  methods: {
    updateWidth() {
      if (this.$refs.wrap) {
        const w = this.$refs.wrap.clientWidth;
        if (w > 0) this.containerWidth = w;
      }
    },

    setSeriesVisible(key, visible) {
      // Spread создаёт новый объект — Vue 3 реактивно отслеживает изменение
      this.hiddenSeries = { ...this.hiddenSeries, [key]: !visible };
    },

    formatAxisValue(v) {
      if (this.formatValue) return this.formatValue(v, "axis");
      const n = Number(v);
      if (Math.abs(n) >= 1_000_000)
        return (n / 1_000_000).toFixed(1) + "М";
      if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(0) + "К";
      return Math.round(n).toString();
    },

    formatTooltipValue(v) {
      if (this.formatValue) return this.formatValue(v, "tooltip");
      return Number(v).toLocaleString("ru-RU");
    },

    onMouseMove(event) {
      if (!this.chartData) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const svgX =
        ((event.clientX - rect.left) / rect.width) * this.chartData.W;

      let closest = null;
      let minDist = Infinity;
      this.chartData.points.forEach((pt) => {
        const d = Math.abs(svgX - pt.x);
        if (d < minDist) {
          minDist = d;
          closest = pt;
        }
      });
      if (!closest) return;

      const wrapRect = this.$refs.wrap.getBoundingClientRect();
      let left = event.clientX - wrapRect.left + 14;
      if (left + 190 > wrapRect.width)
        left = event.clientX - wrapRect.left - 200;

      this.tooltip = {
        visible: true,
        svgX: closest.x,
        left,
        top: Math.max(0, event.clientY - wrapRect.top - 60),
        label: [closest.label?.date, closest.label?.time]
          .filter(Boolean)
          .join(", "),
        values: closest.values,
        isEmpty: !closest.hasData,
      };
    },
  },

  mounted() {
    this.$nextTick(() => {
      const wrap = this.$refs.wrap;

      if (!wrap) return;

      this.updateWidth();

      this._ro = new ResizeObserver(() => {
        if (this._rafId) cancelAnimationFrame(this._rafId);

        this._rafId = requestAnimationFrame(() => {
          this.updateWidth();
        });
      });

      this._ro.observe(wrap);
    });
  },

  beforeUnmount() {
    if (this._rafId) cancelAnimationFrame(this._rafId);

    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }
  },
};
</script>

<style scoped>
.line-chart {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.line-chart__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.line-chart__svg {
  display: block;
  width: 100%;
  height: auto;
  cursor: crosshair;
  overflow: visible;
}

.line-chart__axis-label {
  font-size: 11px;
  fill: #aaa;
  font-family: "Roboto", sans-serif;
}

.line-chart__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #aaa;
  font-size: 14px;
  font-style: italic;
  border: 1.5px dashed #e5e7eb;
  border-radius: 10px;
}

.line-chart__tooltip {
  position: absolute;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
  z-index: 10;
  min-width: 180px;
}

.line-chart__tooltip-period {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  margin-bottom: 2px;
}

.line-chart__tooltip-row {
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-dark);
}

.line-chart__tooltip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.line-chart__tooltip-empty {
  font-size: 12px;
  color: #aaa;
  font-style: italic;
}
</style>
